import { useState, useEffect } from "react";
import { Form, Input, Button, message, Result } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { LockOutlined } from "@ant-design/icons";

function ResetPassword() {
  const navigate = useNavigate();
  const { token } = useParams();
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setTokenValid(false);
        setValidating(false);
        return;
      }

      try {
        const response = await fetch(`/api/auth/validate-reset-token/${token}`);
        const data = await response.json();

        setTokenValid(response.ok && data.success);
      } catch (error) {
        setTokenValid(false);
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const handleResetPassword = async (values) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/auth/reset-password/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: values.password }),
      });

      const data = await response.json();

      if (response.ok) {
        messageApi.success("Password reset successfully!");
        form.resetFields();

        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      } else {
        messageApi.error(data.message || "Failed to reset password");
      }
    } catch (error) {
      messageApi.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#ffffff",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            className="spinner"
            style={{
              border: "4px solid #f3f3f3",
              borderTop: "4px solid black",
              borderRadius: "50%",
              width: "40px",
              height: "40px",
              animation: "spin 1s linear infinite",
              margin: "0 auto",
            }}
          />
          <p style={{ marginTop: "20px", color: "#666" }}>
            Validating reset link...
          </p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#ffffff",
        }}
      >
        <Result
          status="error"
          title="Invalid or Expired Link"
          subTitle="This password reset link is invalid or has expired. Please request a new one."
          extra={[
            <Button
              type="primary"
              key="login"
              onClick={() => navigate("/login")}
              style={{
                background: "black",
                borderRadius: "8px",
                height: "45px",
                paddingInline: "32px",
              }}
            >
              Back to Login
            </Button>,
          ]}
        />
      </div>
    );
  }

  return (
    <>
      {contextHolder}
      <div
        className="reset-password-container"
        style={{
          minHeight: "100vh",
          background: "#ffffff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          className="container"
          style={{
            maxWidth: "520px",
            width: "100%",
            background:
              "linear-gradient(29deg, rgba(0, 240, 251, 1) 0%, rgba(255,255,255,1) 50%, rgba(0, 240, 251, 1) 100%)",
            borderRadius: "20px",
            padding: "40px 30px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
          }}
        >
          {/* Header Section */}
          <div style={{ textAlign: "center", marginBottom: "40px" }}>
            <div
              style={{
                width: "60px",
                height: "60px",
                background: "black",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 20px",
              }}
            >
              <LockOutlined style={{ fontSize: "28px", color: "white" }} />
            </div>
            <h1
              className="font-cinzel"
              style={{
                color: "black",
                fontSize: "clamp(24px, 5vw, 32px)",
                fontWeight: "500",
                margin: "0 0 10px",
              }}
            >
              RESET PASSWORD
            </h1>
            <p
              style={{
                color: "black",
                fontFamily: "'Outfit', sans-serif",
                fontSize: "14px",
                fontWeight: "300",
                opacity: 0.9,
                margin: 0,
              }}
            >
              Enter your new password below
            </p>
          </div>

          <Form
            form={form}
            name="resetPasswordForm"
            layout="vertical"
            onFinish={handleResetPassword}
            style={{ width: "100%" }}
          >
            {/* New Password */}
            <Form.Item
              label={
                <span
                  style={{
                    fontFamily: "'Outfit', sans-serif",
                    fontSize: "15px",
                    fontWeight: "500",
                    color: "black",
                    letterSpacing: "0.5px",
                  }}
                >
                  New Password
                </span>
              }
              name="password"
              rules={[
                { required: true, message: "Please enter your new password" },
                { min: 6, message: "Password must be at least 6 characters" },
              ]}
            >
              <Input.Password
                size="large"
                style={{
                  background: "rgba(255, 255, 255, 0.05)",
                  color: "#333",
                  border: "1px solid #999999",
                  borderRadius: "12px",
                  padding: "12px 16px",
                  fontSize: "15px",
                  height: "48px",
                  transition: "all 0.3s ease",
                }}
                placeholder="Enter new password..."
                onFocus={(e) => {
                  e.target.style.borderColor = "black";
                  e.target.style.boxShadow = "0 0 0 2px rgba(255, 169, 0, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#999999";
                  e.target.style.boxShadow = "none";
                }}
              />
            </Form.Item>

            {/* Confirm Password */}
            <Form.Item
              label={
                <span
                  style={{
                    fontFamily: "'Outfit', sans-serif",
                    fontSize: "15px",
                    fontWeight: "500",
                    color: "black",
                    letterSpacing: "0.5px",
                  }}
                >
                  Confirm Password
                </span>
              }
              name="confirmPassword"
              dependencies={["password"]}
              rules={[
                { required: true, message: "Please confirm your password" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error("The passwords do not match")
                    );
                  },
                }),
              ]}
            >
              <Input.Password
                size="large"
                style={{
                  background: "rgba(255, 255, 255, 0.05)",
                  color: "#333",
                  border: "1px solid #999999",
                  borderRadius: "12px",
                  padding: "12px 16px",
                  fontSize: "15px",
                  height: "48px",
                  transition: "all 0.3s ease",
                }}
                placeholder="Confirm new password..."
                onFocus={(e) => {
                  e.target.style.borderColor = "black";
                  e.target.style.boxShadow = "0 0 0 2px rgba(255, 169, 0, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#999999";
                  e.target.style.boxShadow = "none";
                }}
              />
            </Form.Item>

            {/* Submit Button */}
            <Form.Item style={{ marginBottom: "20px" }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                className="font-cinzel"
                style={{
                  background: "black",
                  color: "#fff",
                  width: "100%",
                  borderRadius: "12px",
                  height: "50px",
                  fontSize: "16px",
                  fontWeight: "500",
                  border: "none",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = "translateY(-2px)";
                  e.target.style.boxShadow =
                    "0 6px 20px rgba(255, 169, 0, 0.4)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = "none";
                }}
              >
                {loading ? "Resetting..." : "Reset Password"}
              </Button>
            </Form.Item>

            <div style={{ textAlign: "center" }}>
              <Button
                type="link"
                onClick={() => navigate("/login")}
                style={{
                  color: "black",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Back to Login
              </Button>
            </div>
          </Form>
        </div>
      </div>

      <style jsx>{`
        @media (max-width: 768px) {
          .reset-password-container .container {
            padding: 30px 20px;
            margin: 20px;
          }
        }

        @media (max-width: 480px) {
          .reset-password-container .container {
            padding: 25px 15px;
            margin: 15px;
          }
        }
      `}</style>
    </>
  );
}

export default ResetPassword;
